import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Edit, Delete } from '@mui/icons-material';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import PropTypes from 'prop-types';
import { Box, Popover, ListItemText, Avatar, Typography, Tooltip, ListItem, IconButton } from '@mui/material';

import { authContext } from '../../routes';
import axiosInstance from '../../api';
import { fToNowAmh } from '../../utils/formatTime';
import SERVER_API_URL from '../../config';
import { EmptyPersonalChat, EmptyGroupChat, EmptyPublicChat } from './emptyChat';
import Iconify from '../../components/iconify';
import File from './file/File';

export default function ChatBody({ chats, targetType, targetName, handleEdit, handleDelete, canEdit, canDelete }) {
  const { user } = useContext(authContext);
  const [avatarUrls, setAvatarUrls] = useState({});
  const [avatars, setAvatars] = useState({});
  const [usersReadOne, setUsersReadOne] = useState([]);
  const [open, setOpen] = useState(null);
  const boxRef = useRef(null);

  const getChatAvatars = useCallback(
    (chats) => {
      if (Array.isArray(chats)) {
        chats.forEach((chat) => {
          if (!avatarUrls[chat.sender.id]) {
            getAvatarById(chat.sender.id)
              .then((url) => {
                setAvatarUrls((prevUrls) => ({
                  ...prevUrls,
                  [chat.sender.id]: url,
                }));
              })
              .catch((error) => {
                console.error('Error fetching avatar::a', error);
              });
          }
        });
      }
    },
    [avatarUrls]
  );

  useEffect(() => {
    getChatAvatars(chats);
  }, [chats, getChatAvatars]);

  useEffect(() => {
    if (boxRef.current && chats && chats.length > 0) {
      const boxElement = boxRef.current;
      boxElement.scrollTop = boxElement.scrollHeight;
    }
  }, [chats]);

  const getAvatarById = async (id) => {
    try {
      const response = await axiosInstance.get(`${SERVER_API_URL}/avatar/getAvatarById/${id}`, {
        responseType: 'blob',
      });
      if (response.status === 200) {
        return URL.createObjectURL(response.data);
      }
      throw new Error('Failed to fetch avatar ff');
    } catch (error) {
      console.error('Error fetching avatar:ff', error);
      throw error;
    }
  };

  const handleReaders = async (chatId) => {
    try {
      const response = await axiosInstance.post(`${SERVER_API_URL}/chat/usersReadOne/${chatId}`);
      if (response.status === 200) {
        setUsersReadOne(response.data);
        response.data.forEach(({ reader }) => {
          if (!avatars[reader.id]) {
            getAvatarById(reader.id)
              .then((url) => {
                setAvatars((prevUrls) => ({
                  ...prevUrls,
                  [reader.id]: url,
                }));
              })
              .catch((error) => {
                console.error('Error fetching avatar:nn', error);
              });
          }
        });
      }
    } catch (error) {
      console.error('Error fetching readers:ff', error);
    }
  };

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  return (
    <Box
      ref={boxRef}
      sx={{
        overflow: 'scroll',
        scrollbarWidth: 'none',
        '-ms-overflow-style': 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        height: 'calc(100%)',
        border: 2,
        borderColor: 'divider',
        pl: 2,
        pr: 2,
        pb: 2
      }}
    >
      {chats && chats.length >= 1 ? (
        chats.map((chat) => {
          const isOwn = user && chat.sender.id === user.id;
          const avatarUrl = avatarUrls[chat.sender.id];

          return (
            <Box key={chat.id} align={isOwn ? 'right' : 'left'}>
              <Box align={isOwn ? 'right' : 'left'} sx={{ width: "70%" }}>
                <Box
                  sx={{
                    width: 'fit-content',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    mt: 2,
                    alignItems: 'end'
                  }}
                  align="left"
                >
                  {!isOwn && targetType !== 'personal' && (
                    <Avatar src={avatarUrl} alt={`${chat.sender.firstName} ${chat.sender.lastName}'s avatar`} />
                  )}
                  <Box
                    sx={{
                      width: 'fit-content',
                      px: 2,
                      py: 1,
                      backgroundColor: isOwn
                        ? (theme) => (theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.light)
                        : (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[500]),
                      borderRadius: isOwn ? '25px 25px 0 25px' : '25px 25px 25px 0',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isOwn ? 'end' : 'start',
                      gap: 1,
                    }}
                  >
                    {chat.file && (<File file={chat.file} />)}
                    <Typography>{chat.body}</Typography>
                    <Typography color="text.secondary">
                      {chat.sender.firstName} {chat.sender.lastName} - {fToNowAmh(chat.updatedAt)}{' '}
                      {chat.createdAt !== chat.updatedAt && 'ተስተካክሏል'}{' '}
                      {isOwn && (
                        <>
                          {canEdit && (
                            <Tooltip title={canEdit ? 'አርትዕ' : 'ይህን መልእክት ከማርትዕ ታግደሃል!'} color="warning" arrow>
                              <IconButton onClick={() => handleEdit(chat.id, chat.body)}>
                                <Edit sx={{ fontSize: '15px' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip
                              title={
                                canDelete
                                  ? 'ሰርዝ'
                                  : 'ይህን መልእክት ከመሰረዝ ታግደዋል!'
                              }
                              color='error'
                              arrow
                            >
                              <IconButton disabled={!canDelete} onClick={() => handleDelete(chat.id)}>
                                <Delete sx={{ fontSize: '15px' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {user && user.role.permissions.chatReadHistory.read && (
                            <Tooltip title="አንባብያን" color="success" arrow>
                              <IconButton onClick={() => handleReaders(chat.id)}>
                                <CheckCircleTwoToneIcon onClick={handleOpenMenu} sx={{ fontSize: '15px' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })
      ) : (
        <>
          {targetType === 'public' && <EmptyPublicChat target={targetName} />}
          {targetType === 'district' && <EmptyGroupChat target={`${targetName} ወረዳ`} />}
          {targetType === 'church' && <EmptyGroupChat target={`${targetName} አጥቢያ`} />}
          {targetType === 'role' && <EmptyGroupChat target={`${targetName} ሚና`} />}
          {targetType === 'category' && <EmptyGroupChat target={`${targetName} ምድብ`} />}
          {targetType === 'personal' && <EmptyPersonalChat target={targetName} />}
        </>
      )}
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        {usersReadOne && usersReadOne.length >= 1 ? (
          usersReadOne.map((chat) => (
            <Box key={chat.reader.id} sx={{ display: 'flex' }}>
              <Avatar src={avatars[chat.reader.id]} alt={`${chat.reader.lastName}'s avatar`} />
              <Box sx={{ ml: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                  {chat.reader.firstName} {chat.reader.lastName}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <Iconify color={chat.reader.category.color} icon="ic:twotone-family-restroom" /> -{' '}
                  {chat.reader.role.name}
                </Typography>
              </Box>
            </Box>
          ))
        ) : (
          <ListItem sx={{ py: 1.5, px: 2.5, mt: '1px' }}>
            <ListItemText primary="ያነበበው የለም!" />
          </ListItem>
        )}
      </Popover>
    </Box>
  );
}

ChatBody.propTypes = {
  chats: PropTypes.array.isRequired,
  targetType: PropTypes.string.isRequired,
  targetName: PropTypes.string.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
};
